/**
 * +quests: the quest log (hotkey Y, or the tracker's "Log" button): the main quest's current step
 * on top, then every accepted side quest with its level, area, objective and progress, a Track
 * button (the waypoint arrow follows it) and Abandon (asks once more). DOM only; the world layer
 * (world/sideQuests.ts) supplies the rows and does the intents.
 */
import { el } from '../dom';
import { openTownPanel, type TownPanel } from '../field/townPanel';
import './quests.css';

export interface QuestLogRow {
  id: string;
  title: string;
  level: number;
  area: string;
  objective: string;
  progress: string;
  ready: boolean;
  grey: boolean;
  tracked: boolean;
  /** "Return to Farmer Hobb Turvey" once ready. */
  hint?: string;
}

export interface QuestLogDeps {
  host: HTMLElement;
  /** The main quest's step (null when the chain is done). */
  main(): { title: string; text: string } | null;
  rows(): QuestLogRow[];
  max: number;
  track(id: string): void;
  abandon(id: string): Promise<boolean>;
  onClose?(): void;
}

export function openQuestLog(deps: QuestLogDeps): TownPanel {
  const panel = openTownPanel(deps.host, { title: 'Quest Log', subtitle: 'Mossvale', className: 'fx-qlog', ...(deps.onClose ? { onClose: deps.onClose } : {}) });
  const render = (): void => {
    if (panel.closed) return;
    panel.body.replaceChildren();
    const main = deps.main();
    if (main) {
      el('div', 'fx-qlog-sec', panel.body, 'Main quest');
      const m = el('div', 'fx-qlog-q main', panel.body);
      el('div', 'fx-qlog-t', m, main.title);
      el('div', 'fx-qlog-o', m, main.text);
    }
    const rows = deps.rows();
    el('div', 'fx-qlog-sec', panel.body, `Side quests (${rows.length}/${deps.max})`);
    if (!rows.length) el('div', 'fx-qlog-empty', panel.body, 'No side quests yet. Look for a gold "!" over the locals along the road.');
    for (const r of rows) {
      const q = el('div', `fx-qlog-q${r.ready ? ' ready' : ''}${r.grey ? ' grey' : ''}`, panel.body);
      const t = el('div', 'fx-qlog-t', q, r.title);
      el('small', '', t, `Lv ${r.level} · ${r.area}`);
      el('div', 'fx-qlog-o', q, r.objective);
      el('div', 'fx-qlog-p', q, r.ready && r.hint ? r.hint : r.progress);
      const row = el('div', 'fx-qlog-row', q);
      const tr = el('button', 'fx-btn ghost', row, r.tracked ? 'Untrack' : 'Track');
      tr.type = 'button';
      tr.addEventListener('click', () => {
        deps.track(r.id);
        render();
      });
      const ab = el('button', 'fx-btn danger', row, 'Abandon');
      ab.type = 'button';
      let armed = false;
      ab.addEventListener('click', () => {
        if (!armed) {
          armed = true;
          ab.textContent = 'Really abandon?';
          return;
        }
        ab.disabled = true;
        void deps.abandon(r.id).then(() => render());
      });
    }
  };
  render();
  const close = el('button', 'fx-btn ghost', panel.foot, 'Close');
  close.type = 'button';
  close.addEventListener('click', () => panel.close());
  return panel;
}
